// Interpolación lineal de paletas hex. Pensado para gráficos data-driven
// donde el número de series/segmentos es dinámico (StoresDistributionCard
// con N items distintos según el grouping activo del tab).
//
// Filosofía: el theme define ANCHOR colors (--report-chart-segment-*). El
// consumer lee los anchors via getComputedStyle y le pide a este util N
// colores evenly-spaced. Para N <= len(stops) devuelve los stops directos;
// para N > len(stops) interpola.

function hexToRgb(hex) {
    if (typeof hex !== "string") return null;
    const cleaned = hex.replace("#", "");
    if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null;
    return {
        r: parseInt(cleaned.slice(0, 2), 16),
        g: parseInt(cleaned.slice(2, 4), 16),
        b: parseInt(cleaned.slice(4, 6), 16),
    };
}

function rgbToHex({ r, g, b }) {
    const h = (v) => v.toString(16).padStart(2, "0");
    return `#${h(r)}${h(g)}${h(b)}`;
}

function lerpChannel(a, b, t) {
    return Math.round(a + (b - a) * t);
}

/**
 * Genera N colores interpolando linealmente entre `from` y `to` (hex strings).
 * - N <= 0 → []
 * - N === 1 → [from]
 * - N >= 2 → evenly-spaced incluyendo ambos extremos
 *
 * Si alguno de los hex no parsea, devuelve N entradas con el sentinel '#000000'
 * para que el consumer pueda detectar el error visualmente.
 */
export function interpolateHexPalette(from, to, n) {
    if (n <= 0) return [];
    if (n === 1) return [from];

    const a = hexToRgb(from);
    const b = hexToRgb(to);
    if (!a || !b) return new Array(n).fill("#000000");

    return Array.from({ length: n }, (_, i) => {
        const t = i / (n - 1);
        return rgbToHex({
            r: lerpChannel(a.r, b.r, t),
            g: lerpChannel(a.g, b.g, t),
            b: lerpChannel(a.b, b.b, t),
        });
    });
}

function clampChannel(v) {
    return Math.max(0, Math.min(255, Math.round(v)));
}

// Mezcla un hex hacia negro por `t` ∈ [0,1] (0 = igual, 1 = negro). Hex
// inválido → se devuelve tal cual.
export function darken(hex, t) {
    const c = hexToRgb(hex);
    if (!c) return hex;
    return rgbToHex({
        r: clampChannel(c.r * (1 - t)),
        g: clampChannel(c.g * (1 - t)),
        b: clampChannel(c.b * (1 - t)),
    });
}

// Mezcla un hex hacia blanco por `t` ∈ [0,1] (0 = igual, 1 = blanco).
export function lighten(hex, t) {
    const c = hexToRgb(hex);
    if (!c) return hex;
    return rgbToHex({
        r: clampChannel(c.r + (255 - c.r) * t),
        g: clampChannel(c.g + (255 - c.g) * t),
        b: clampChannel(c.b + (255 - c.b) * t),
    });
}

// Cuánto se empujan los extremos del gradiente de series ANTES de interpolar:
// el ancla oscura se oscurece y la clara se aclara. Así los colores intermedios
// quedan más espaciados y las series adyacentes se distinguen mejor — el
// gradiente sigue derivando de los tokens del theme, sólo se ensancha el rango.
// Tuneables en un solo lugar.
const SERIES_DARKEN = 0.30;
const SERIES_LIGHTEN = 0.32;

function luminance(hex) {
    const c = hexToRgb(hex);
    if (!c) return 0;
    return 0.299 * c.r + 0.587 * c.g + 0.114 * c.b;
}

// Paleta de N series del gradiente del theme con el rango ensanchado: el extremo
// más oscuro se oscurece y el más claro se aclara, así los intermedios quedan
// más separados. Agnóstico al orden de los args (la luminancia decide cuál es
// cuál) — sirve tanto a `(dark, light)` (stacked/líneas) como a `(light, dark)`
// (donut). Preserva la dirección que pidió el caller. Mismo contrato que
// `interpolateHexPalette`: N<=0 → []; N===1 → [from] sin tocar.
export function widenedSeriesPalette(from, to, n) {
    if (n <= 1) return interpolateHexPalette(from, to, n);
    const fromIsDarker = luminance(from) <= luminance(to);
    const wFrom = fromIsDarker ? darken(from, SERIES_DARKEN) : lighten(from, SERIES_LIGHTEN);
    const wTo = fromIsDarker ? lighten(to, SERIES_LIGHTEN) : darken(to, SERIES_DARKEN);
    return interpolateHexPalette(wFrom, wTo, n);
}
